import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Heart, ShoppingCart, Star, Gift, Eye, X, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { PLACEHOLDER_IMAGE } from '../lib/placeholder';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const formatPrice = (price) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })
    .format(Number(price) || 0);

const getImageUrl = (url) => {
  if (!url) return PLACEHOLDER_IMAGE;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

/**
 * Catalogue product tile.
 *
 * Rebuilt for density: five to six of these sit in a row on desktop, so every
 * pixel of chrome costs a product the customer never sees. Padding is 10px, the
 * title is clamped to two lines, and the secondary actions (Add to cart, Quick
 * view) live in a hover layer over the image rather than taking permanent
 * vertical space.
 *
 * Hover-only actions are a desktop affordance. On touch there is no hover, so
 * the layer is rendered always-visible below `sm` — otherwise the primary
 * action would be unreachable on a phone.
 */
export function ProductCard({ product }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();
  const [quickView, setQuickView] = useState(false);
  const [adding, setAdding] = useState(false);

  const inWishlist = isInWishlist(product.product_id);
  const isB1G1 = product.discount_type === 'b1g1';
  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;
  const hasOffer = product.original_price > product.price && !isB1G1;
  const outOfStock = Number(product.stock) === 0;

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAdding(true);
    try {
      await addToCart(product.product_id, 1);
      toast.success('Added to cart');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setAdding(false);
    }
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error('Please login to add to wishlist');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    try {
      if (inWishlist) {
        await removeFromWishlist(product.product_id);
        toast.success('Removed from wishlist');
      } else {
        await addToWishlist(product.product_id);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const openQuickView = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setQuickView(true);
  };

  const displayPrice = isB1G1 && parseFloat(product.price) === 0
    ? product.original_price
    : product.price;

  return (
    <>
      <Link
        to={`/product/${product.product_id}`}
        className="group relative flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200/70 bg-white transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_8px_24px_rgba(16,24,40,0.10)]"
        data-testid={`product-card-${product.product_id}`}
      >
        <div className="relative aspect-square overflow-hidden bg-white">
          <img
            src={getImageUrl(product.images?.[0])}
            alt={product.name}
            onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }}
            className="h-full w-full object-contain p-2.5 transition-transform duration-500 group-hover:scale-[1.06]"
            loading="lazy"
            decoding="async"
          />

          {/* Badges — discount and bestseller are the two that change a decision,
              so they lead; the rest follow only when present. */}
          <div className="pointer-events-none absolute left-1.5 top-1.5 flex flex-col items-start gap-1">
            {discount > 0 && !isB1G1 && (
              <span className="rounded-md bg-destructive px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                {product.discount_type === 'flat'
                  ? `-₹${product.original_price - product.price}`
                  : `-${discount}%`}
              </span>
            )}
            {product.is_bestseller && (
              <span className="rounded-md bg-brand-orange px-1.5 py-0.5 text-[10px] font-bold leading-none text-secondary">
                Bestseller
              </span>
            )}
            {product.is_trending && !product.is_bestseller && (
              <span className="rounded-md bg-primary px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                Trending
              </span>
            )}
            {isB1G1 && (
              <span className="flex items-center gap-1 rounded-md bg-indigo-600 px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                <Gift className="h-2.5 w-2.5" /> B1G1
              </span>
            )}
          </div>

          <button
            onClick={handleWishlist}
            aria-label={inWishlist ? 'Remove from wishlist' : 'Add to wishlist'}
            className={`absolute right-1.5 top-1.5 grid h-7 w-7 place-items-center rounded-full transition-all ${
              inWishlist
                ? 'bg-primary text-white'
                : 'bg-white/85 text-secondary backdrop-blur hover:bg-white hover:text-primary'
            }`}
            data-testid={`wishlist-btn-${product.product_id}`}
          >
            <Heart className={`h-3.5 w-3.5 ${inWishlist ? 'fill-current' : ''}`} />
          </button>

          {outOfStock && (
            <span className="absolute inset-x-0 bottom-0 bg-slate-900/80 py-1 text-center text-[10px] font-bold uppercase tracking-wider text-white">
              Out of stock
            </span>
          )}

          {/* Hover actions. Always visible under `sm` — a phone has no hover. */}
          {!outOfStock && (
            <div className="absolute inset-x-1.5 bottom-1.5 flex gap-1 opacity-100 transition-all duration-200 sm:translate-y-1 sm:opacity-0 sm:group-hover:translate-y-0 sm:group-hover:opacity-100">
              <button
                onClick={handleAddToCart}
                disabled={adding}
                className="flex h-8 flex-1 items-center justify-center gap-1.5 rounded-lg bg-brand-orange text-[12px] font-bold text-secondary shadow-sm transition-colors hover:bg-brand-orange-dark disabled:opacity-60"
                data-testid={`add-cart-btn-${product.product_id}`}
              >
                {adding ? <Check className="h-3.5 w-3.5" /> : <ShoppingCart className="h-3.5 w-3.5" />}
                <span className="hidden xs:inline">Add</span>
              </button>
              <button
                onClick={openQuickView}
                aria-label="Quick view"
                className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg bg-white/95 text-secondary shadow-sm backdrop-blur transition-colors hover:bg-white hover:text-primary"
                data-testid={`quick-view-btn-${product.product_id}`}
              >
                <Eye className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col p-2.5">
          {product.brand && (
            <p className="truncate font-body text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              {product.brand}
            </p>
          )}
          <h3 className="mt-0.5 line-clamp-2 min-h-[2.2rem] font-body text-[13px] font-medium leading-[1.15rem] text-secondary transition-colors group-hover:text-primary">
            {product.name}
          </h3>

          {!!product.review_count && product.review_count > 0 && (
            <div className="mt-1 flex items-center gap-1">
              <span className="flex items-center gap-0.5 rounded bg-success/10 px-1 py-px text-[10px] font-bold text-success">
                {product.average_rating}
                <Star className="h-2.5 w-2.5 fill-current" />
              </span>
              <span className="text-[10px] text-muted-foreground">({product.review_count})</span>
            </div>
          )}

          <div className="mt-auto flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 pt-1.5">
            <span className="font-body text-[15px] font-bold text-secondary">
              {formatPrice(displayPrice)}
            </span>
            {hasOffer && (
              <>
                <span className="text-[11px] text-muted-foreground line-through">
                  {formatPrice(product.original_price)}
                </span>
                <span className="text-[11px] font-bold text-success">
                  {product.discount_type === 'flat'
                    ? `₹${product.original_price - product.price} off`
                    : `${discount}% off`}
                </span>
              </>
            )}
          </div>
        </div>
      </Link>

      {quickView && (
        <QuickView
          product={product}
          onClose={() => setQuickView(false)}
          onAddToCart={handleAddToCart}
          adding={adding}
        />
      )}
    </>
  );
}

/**
 * Quick view.
 *
 * Renders only fields the listing response already carries — images, name,
 * brand, price, rating, stock, short_description. It makes no request, so it
 * cannot show anything the card did not already have; the full page stays one
 * click away for specifications and reviews.
 */
function QuickView({ product, onClose, onAddToCart, adding }) {
  const [active, setActive] = useState(0);
  const images = (product.images?.length ? product.images : [null]).slice(0, 5);
  const isB1G1 = product.discount_type === 'b1g1';
  const discount = product.original_price
    ? Math.round((1 - product.price / product.original_price) * 100)
    : 0;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Quick view — ${product.name}`}
    >
      <div
        className="relative flex max-h-[85vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:flex-row"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close quick view"
          className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-full bg-white/90 text-secondary shadow-sm transition-colors hover:bg-white hover:text-destructive"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex w-full flex-col gap-2 bg-slate-50 p-4 sm:w-1/2">
          <div className="flex aspect-square items-center justify-center overflow-hidden rounded-xl bg-white">
            <img
              src={getImageUrl(images[active])}
              alt={product.name}
              onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER_IMAGE) e.currentTarget.src = PLACEHOLDER_IMAGE; }}
              className="max-h-full max-w-full object-contain p-4"
            />
          </div>
          {images.length > 1 && (
            <div className="flex gap-1.5">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  aria-label={`Image ${i + 1}`}
                  className={`h-11 w-11 overflow-hidden rounded-lg border bg-white transition-colors ${
                    active === i ? 'border-primary' : 'border-slate-200 hover:border-slate-300'}`}
                >
                  <img src={getImageUrl(img)} alt="" className="h-full w-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex w-full flex-col overflow-y-auto p-5 sm:w-1/2">
          {product.brand && (
            <p className="font-body text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {product.brand}
            </p>
          )}
          <h2 className="mt-1 font-body text-lg font-bold leading-snug text-secondary">{product.name}</h2>

          {!!product.review_count && product.review_count > 0 && (
            <div className="mt-2 flex items-center gap-1.5">
              <span className="flex items-center gap-0.5 rounded bg-success/10 px-1.5 py-0.5 text-[11px] font-bold text-success">
                {product.average_rating} <Star className="h-3 w-3 fill-current" />
              </span>
              <span className="text-[11px] text-muted-foreground">{product.review_count} reviews</span>
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-baseline gap-2">
            <span className="font-body text-2xl font-bold text-secondary">
              {formatPrice(isB1G1 && parseFloat(product.price) === 0 ? product.original_price : product.price)}
            </span>
            {product.original_price > product.price && !isB1G1 && (
              <>
                <span className="text-sm text-muted-foreground line-through">
                  {formatPrice(product.original_price)}
                </span>
                <span className="text-sm font-bold text-success">{discount}% off</span>
              </>
            )}
          </div>

          {product.short_description && (
            <p className="mt-3 font-body text-[13px] leading-relaxed text-muted-foreground line-clamp-4">
              {product.short_description}
            </p>
          )}

          <p className={`mt-3 font-body text-[12px] font-semibold ${
            Number(product.stock) === 0 ? 'text-destructive' : 'text-success'}`}>
            {Number(product.stock) === 0
              ? 'Out of stock'
              : Number(product.stock) <= 5 ? `Only ${product.stock} left` : 'In stock'}
          </p>

          <div className="mt-auto flex gap-2 pt-5">
            <button
              onClick={onAddToCart}
              disabled={adding || Number(product.stock) === 0}
              className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-brand-orange font-body text-sm font-bold text-secondary transition-colors hover:bg-brand-orange-dark disabled:opacity-50"
            >
              <ShoppingCart className="h-4 w-4" /> Add to cart
            </button>
            <Link
              to={`/product/${product.product_id}`}
              className="flex h-11 items-center justify-center rounded-xl border border-slate-200 px-4 font-body text-sm font-semibold text-secondary transition-colors hover:bg-slate-50"
            >
              Details
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
